import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { format, parseISO, fromUnixTime, formatDistanceToNow } from 'date-fns';
import {CopyToClipboard} from 'react-copy-to-clipboard';

import mixpanel from 'mixpanel-browser';

// Firebase
import { db, functions } from '../../config/firebase-config';
import { collection, getDocs, getDoc, addDoc, updateDoc, doc, setDoc, deleteDoc, query, where, onSnapshot, orderBy, startAfter, limit } from 'firebase/firestore';
import { httpsCallable } from "firebase/functions";

// Redux
import { connect } from 'react-redux';

// Actions
import { getUserById } from '../../actions/authActions';
import { addLike_Review, deleteReview } from '../../actions/postActions';
import { toggleAuthModal } from '../../actions/navActions';
import { setAlert } from '../../actions/alertActions';
import { createChat } from '../../actions/chatActions';

// components - imported
import { Avatar, Button } from '@material-ui/core';
import ImageBlock from '../common/ImageBlock';
import DefaultAvatar from 'react-avatar';
import Verification_Badge from '../common/Verification_Badge';
import Tag from '../common/Tag';

// Icons - imported
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import RepeatIcon from '@material-ui/icons/Repeat';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import FavoriteIcon from '@material-ui/icons/Favorite';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import ShareIcon from '@material-ui/icons/Share';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import LinkIcon from '@material-ui/icons/Link';
import CheckIcon from '@material-ui/icons/Check';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';
import MailIcon from '@material-ui/icons/Mail';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';

// Modal
import Modal from '../modal/Modal';
import ModalContainer from '../modal/ModalContainer';

// DraftJS
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";

// Constant Variables
import MAIN_FEEDS from '../../utils/constant_variables/MAIN_FEEDS';

const Review = ({ 
    postDoc, 
    auth, 
    post: {
        post,
        hiddenComments
    },
    nav: {
        page,
        website,
        website_loading
    },
    getUserById, 
    parentPost,
    addLike_Review, 
    deleteReview, 
    setAlert, 
    createChat, 
    toggleAuthModal,
    executeScroll,
    isPreview,
    isComment
}) => {

    // Window Width
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Tracks the like status of post
    const [liked, setLiked] = useState(false);
    // Flag for checking if like is properly updated
    const [checkLike, setCheckLike] = useState(false);

    // Flag for checking if group's name should display as author of post
    const [showGroupName, setShowGroupName] = useState(false);

    // --- Convert DraftJS text to HTML text

    // Tracks whether the text has been converted
    const [gotText, setGotText] = useState(false);
    // Stores the converted text
    const [textState, setTextState] = useState(null);
    const [rawTextState, setRawTextState] = useState(null);

    // Check if post has image - for font size reasons
    const [hasImg, setHasImg] = useState(false);
    // Flag for checking if hasImg is properly updated
    const [checkHasImg, setCheckHasImg] = useState(false);
    const [imgHeight, setImgHeight] = useState(200);

    // Toggle Dropdwon
    const [dropdown, setDropdown] = useState(false);
    const [shareDropdown, setShareDropdown] = useState(false);

    // Stores the height of a toggle menu
    const [menuHeight, setMenuHeight] = useState(null);

    // Delete post Modal toggles
    const [deleteModal, setDeleteModal] = useState(false);

    // Toggle 'start convo' modal
    const [messageModal, setMessageModal] = useState(false);

    // Tracks the status of copying
    const [copied, setCopied] = useState(false);

    // Controls the visibility of a copied modal
    const [copiedModal, setCopiedModal] = useState(false);

    // Get parent post for like page purposes and displaying the 'replying to'
    const [gotParentPost, setGotParentPost] = useState(false);

    const [replyToPost, setReplyToPost] = useState(null);

    // -- Text Truncation Functions ---

    // is text expanded?
    const [isExpanded, setIsExpanded] = useState(false);
    const CHARACTER_LIMIT = 200;
    const [shouldTruncate, setShouldTruncate] = useState(false);

    // -- END: Text Truncation Functions ---
    
    useEffect(() => {

        // Add an event listener for window resize
        window.addEventListener('resize', () => handleWindowSizeChange());

        // Clean up the event listener on component unmount
        return () => window.removeEventListener('resize', () => handleWindowSizeChange());
    }, []);

    // Clears the copied modal after a delay
    useEffect(() => {
        setTimeout(() => {
            setCopiedModal(false);
        }, 2000);
    }, [copiedModal]);

    // Resets the share dropdown and copied status after a delay
    useEffect(() => {
        setTimeout(() => {
            setShareDropdown(false);
            setCopied(false);
        }, 1000);
    }, [copied]);

    const getParentPost = async () => {
        const docRef = doc(db, 'reviews', postDoc.post_commented_on);
        const postData = await getDoc(docRef);
        setReplyToPost({ ...postData.data(), _id: postData.id });
    }

    if(!gotParentPost && postDoc.post_commented_on) {
        if(!parentPost?.user) {
            getParentPost();
        } else {
            setReplyToPost(parentPost)
        }

        setGotParentPost(true);
    }

    // Update the window width state on window resize
    const handleWindowSizeChange = () => {
        setWindowWidth(window.innerWidth);
    };

    // Determine window size breakpoints
    const isMobile = windowWidth <= 769;
    const isTablet = windowWidth <= 1000;

    // ----- Post Actions Logic -------
    
    // Handle post like action
    const handleLike = (reviewId) => {
        if(isPreview) return;

        if(auth.user) {
            addLike_Review({ reviewId, fromUserData: auth.user, reviewData: postDoc });

            // Toggles the like status
            setLiked(!liked);
        } else {

            // Retrieves user information by ID
            getUserById(postDoc.user._id);

            // Toggles the authentication modal for liking
            toggleAuthModal('like');
        }
    }

    // Closes the dropdown menu
    const toggleCopiedModal = () => {
        setDropdown(false)
        
        // Sets an alert message indicating that the content has been copied to the clipboard
        // setCopiedModal(!copiedModal);
        setAlert('Copied to clipboard', `${!auth.loading && auth.isAuthenticated ? 'okay' : 'success'}`);
        
    }

    // Closes the share dropdown
    const handleShareCopy = () => {
        setShareDropdown(false);
        
        // Sets an alert message indicating that the content has been copied to the clipboard
        // setCopiedModal(!copiedModal);
        setAlert('Copied to clipboard', `${!auth.loading && auth.isAuthenticated ? 'okay' : 'success'}`);
    }

    // Toggle the delete post confirmation modal
    const toggleDeleteModal = () => {
        setDropdown(false)

        // Toggles the delete modal
        setDeleteModal(!deleteModal); 
    }

    // Deletes the post
    const handleDelete = () => {
        if(postDoc) {
            let stored_id = postDoc._id
            let storedParent_id = postDoc.post_commented_on

            deleteReview(postDoc._id);

            // Mixpanel event tracking for deleting a post
            if (process.env.NODE_ENV === 'production') {
                if(!isComment) {
                    mixpanel.track("Delete Review", {
                        "Review ID": `${stored_id}`
                    });
                } else {
                    mixpanel.track("Delete Review Comment", {
                        "Review ID": `${storedParent_id}` // ID of the review the comment was replying to
                    });
                }
            }
        }

        // Closes the delete modal
        setDeleteModal(false);
    }

    // ----- END: Post Actions Logic -------

    const handleProfileVisit = async () => {
        if(!isPreview) {

            console.log('About to Visit Profile..');

            window.location.href = `/user/${postDoc.user._id}`;
        }
    }

    // Message Logic
    
    const toggleMessageModal = () => {
        setDropdown(false);

        // Get user by ID if the message modal is not currently open
        if(!messageModal) getUserById(postDoc.user._id);
        
        setMessageModal(!messageModal);
    }

    const continueMessage = () => {
        if(auth.user) {

            // If the user is authenticated, create a new chat
            createNewChat();
        } else {

            // If the user is not authenticated, close the message modal and show the authentication modal
            setMessageModal(false);
            toggleAuthModal('message');
        }
    }

    const createNewChat = async () => {
        try {
            const chatsCollectionRef = collection(db, "chats");

            // Check if chat with 2 users already exists by chat ID

            console.log('CHECK USER CHAT HERE')
            
            // Query chats where the current user is the from_user and the modal user is the to_user
            const chatsQuery = query(chatsCollectionRef, where("from_user._id", "==", auth.user._id), where("to_user._id", "==", auth.modalUser._id));

            // Query chats where the modal user is the from_user and the current user is the to_user
            const otherChatsQuery = query(chatsCollectionRef, where("to_user._id", "==", auth.user._id), where("from_user._id", "==", auth.modalUser._id));

            // Query chats where the current user is the from_user and the modal user is the to_user
            const chatsSnapshot = await getDocs(chatsQuery);

            // Map the document snapshots to an array of chat objects, including the document ID
            const chats = chatsSnapshot.docs.map((doc) => ({...doc.data(), _id: doc.id}));

            // Query chats where the modal user is the from_user and the current user is the to_user
            const otherChatsSnapshot = await getDocs(otherChatsQuery);

            // Map the document snapshots to an array of chat objects, including the document ID
            const otherChats = otherChatsSnapshot.docs.map((doc) => ({...doc.data(), _id: doc.id}));

            console.log('CHECKING CHATS DATA');
            console.log(chats);
            
            console.log('CHECKING OTHER CHATS DATA');
            console.log(otherChats);

            console.log('CHECK USER CHAT HERE PART 2')

            let chatRoomId;

            // If array length > 0 then there is already existing chat & redirect instead
            
            if(!(chats.length > 0 || otherChats.length > 0)) {

                // Create a new chat if no existing chat is found
                chatRoomId = await createChat(auth.modalUser, auth.user, postDoc._id);
            }

            // -- Redirect to chat page --

            if(chats.length > 0) {
                window.location.href = `/messages/${chats[0]._id}`;
            } else if (otherChats.length > 0) {
                window.location.href = `/messages/${otherChats[0]._id}`;
            } else {
                // Redirect to the chat page with the newly created chat room ID
                if(chatRoomId) {
                    window.location.href = `/messages/${chatRoomId}`;
                } else {
                    // Fallback: If something goes wrong go to messages page
                    window.location.href = `/messages`;
                }
            }

            // -- END: Redirect to chat page --

            setMessageModal(!messageModal);
            
            console.log('SHOULD BE DONE CREATING');
        } catch (error) {
            console.log('Failed to create new chat');
            console.log(error);
            setAlert('Unable to start conversation', 'danger');
        }
    }

    // End

    // -- Text Truncation Functions ---
    
    // Helper function
    const getPlainText = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    // -- END: Text Truncation Functions ---

    // Function to Capitalize the first letter of a string
    const capitalizeFirstLetter =(string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    const detect_Links_phoneNumbers_Addresses =(string) => {
        // Regex to match URLs
        const urlRegex = /(?<![@\w])(?:https?:\/\/|www\.)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?(?![\w@])/gi;
        
        // Regex for phone numbers (basic US-style, adjust if needed)
        // Matches formats like: 123-456-7890, (123) 456-7890, 1234567890, +1 123 456 7890
        const phoneRegex = /(\+?\d{1,3}[-.\s]?)?(\(?\d{3}\)?[-.\s]?){1,2}\d{4}/g;

        // Regex for addresses (US-style)
        // Matches things like: 123 Main St | 456 Elm Street, Springfield | 789 Broadway Ave, Apt 5, New York
        // TODO: Use a location keyword list to improve detection (i.e. city names, states, countries)
        const addressRegex = /\d{1,5}\s[\w\s]{1,30}\s(?:Street|St|Avenue|Ave|Boulevard|Blvd|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Way|Wy|Plaza|Circle|Ct|Terrace|Ter|Trail|Trl)\b(?:,\s?[\w\s]+)*/gi;

        // 1st wrap URLs with <a> tags
        let temp_result = string.replace(urlRegex, (url) => {

            // If the URL already starts with http:// or https://, leave it
            // Otherwise, prepend http://
            // (purpose: makes sure the link redirects properly)
            const safe_url = /^(https?:\/\/)/i.test(url) ? url : `http://${url}`;

            return `<a href="${safe_url}" class='hoverable_link' target="_blank" rel="noopener noreferrer">${url}</a>`;
        });

        // 2nd replace phone numbers
        temp_result = temp_result.replace(phoneRegex, (phone) => {
            // Remove spaces/dashes for href
            const tel = phone.replace(/[-.\s()]/g, "");
            return `<a href="sms:${tel}" class='hoverable_link'>${phone}</a>`;
        });

        // 3rd replace addresses with Google Maps links
        temp_result = temp_result.replace(addressRegex, (address) => {
            const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
            return `<a href="${href}" class='hoverable_link' target="_blank" rel="noopener noreferrer">${address}</a>`;
        });

        return temp_result;
    }

    // Checks if the user has already liked the post
    if(auth.user && !checkLike) {
        if(postDoc.likes.filter(like => like.user._id.toString() === auth.user_id).length > 0){
            setLiked(true);
        }
        
        // Marks that the check for likes has been performed
        setCheckLike(true);
    }  

    // Converts the DraftJS text to HTML and sets the converted text
    useEffect(() => {
        if (postDoc && !gotText) {
            let unRawState;
            let unRawDescription;

            if(postDoc.text) {
                try {
                    // Convert stringified DraftJS JSON to a raw JS object
                    const rawBlocksData = JSON.parse(postDoc.text);

                    // Extract just the array of text blocks
                    if (rawBlocksData && Array.isArray(rawBlocksData.blocks)) {
                        unRawState = EditorState.createWithContent(
                            convertFromRaw(rawBlocksData)
                        );

                        unRawDescription = draftToHtml(convertToRaw(unRawState.getCurrentContent()));
                    } else {
                        unRawDescription = postDoc.text;
                    }

                    // -- Detect URLs & Phone #'s in text ---

                    unRawDescription = detect_Links_phoneNumbers_Addresses(unRawDescription);
                    
                    // -- END: Detect URLs & Phone #'s in text ---

                    // -- Text Truncation Functions ---

                    // Check if truncation is needed
                    const plainText = getPlainText(unRawDescription);
                    const shouldTruncate_temp = plainText.length > CHARACTER_LIMIT;
                    const truncated_text = plainText.substring(0, CHARACTER_LIMIT) + '...';
                    setShouldTruncate(shouldTruncate_temp);

                    // -- END: Text Truncation Functions ---

                    setRawTextState(unRawDescription);

                    setTextState(detect_Links_phoneNumbers_Addresses(truncated_text));
                } catch (error) {
                    const text_final = detect_Links_phoneNumbers_Addresses(postDoc.text);
                    
                    setRawTextState(text_final);
                    setTextState(text_final);
                }
            }
            
            // Marks that the text conversion has been performed
            setGotText(true);
        }
    }, [postDoc, gotText]);

    // --- ImageKit (img optimization service) ---
    
    // Replace the firebase URL with ImageKit.io URL-endpoint
    let optimized_post_avatar_path = null;

    if(postDoc.user.img) {
        optimized_post_avatar_path = postDoc.user.img.replace("https://firebasestorage.googleapis.com","https://ik.imagekit.io/pba3w2st28/");
    }

    let shareLink = `${website ? website.url : process.env.REACT_APP_DOMAIN}/review/${postDoc._id}`;
    let redirect_on_click = (!isPreview && !isComment);
    
    return (
        <Fragment>
            {/* Render the post component */}
            <div style={(hiddenComments?.length > 0 && hiddenComments?.includes(postDoc._id)) ? {display: 'none'} : {}} className={!(postDoc.commentsOfComments?.length > 0) ? "post" : "post hasPostContainer"}>

                {/* Avatar - left side 3;7 */}
                <div style={{display:'flex', flexDirection:'column'}}>
                    
                    <div className="post__avatar" onClick={handleProfileVisit}>
                        {optimized_post_avatar_path ? (
                            <Avatar src={optimized_post_avatar_path && optimized_post_avatar_path} />
                        ) : (
                            <DefaultAvatar name={showGroupName ? `${postDoc.group.name}` : `${postDoc.user.first_name} ${postDoc.user.last_name}`} />
                        )}
                    </div>
                    
                    {postDoc.commentsOfComments?.length > 0 && (
                        <div className='post_thread_indicator'></div>
                    )}
                </div>

                {/* Post Main Body - right side 3:7 */}
                <div className="post__body">

                    {/* More Button */}
                    <div style={{display: 'flex'}}>
                        
                        <div onClick={() => !isPreview && setDropdown(!dropdown)} className="post__moreBtn">
                            <MoreHorizIcon />
                        </div>
                    </div>

                    {/* Dropdown Menu */}
                    <div className={dropdown ? "edit-dropdown active" : "edit-dropdown"} style={{height: menuHeight}}>
                        <div className="menu">

                            {/* Copy Link Menu Item */}
                            {!isComment && (
                                <CopyToClipboard 
                                    text={shareLink}
                                    onCopy={toggleCopiedModal}
                                >
                                    <div className="menu-item">
                                        {!copied ? (
                                            <Fragment>
                                                <LinkIcon />
                                                Copy link
                                            </Fragment>
                                        ) : (
                                            <Fragment>
                                                <CheckIcon />
                                                Copied!
                                            </Fragment>
                                        )}
                                    </div>
                                </CopyToClipboard>
                            )}

                            {auth.user && postDoc.user._id !== auth.user._id && (
                                <div onClick={toggleMessageModal} className="menu-item">
                                    <MailIcon />
                                    Send Message
                                </div>
                            )}

                            {/* Delete opeion (shown only for authenticated user who created the review) */}
                            {auth.user && postDoc.user._id === auth.user_id ? (
                                <Fragment>
                                    <div onClick={toggleDeleteModal} className="menu-item danger">
                                        <DeleteIcon />
                                        Delete
                                    </div>
                                </Fragment>
                            ) : null}
                        </div>
                    </div>

                    {/* Hidden Overlay for dropdown */}
                    <div
                        className={`hidden-overlay ${dropdown ? "show" : ""}`}
                        onClick={() => setDropdown(!dropdown)}
                    />

                    {/* Post Header */}
                    <div className="post__header">
                        <div className="post__headerText">
                            <h3 className={parentPost ? 'isReply' : ''}>

                                {/* Post Author name */}
                                <span onClick={handleProfileVisit} className='hover_underline'>
                                    <span>{postDoc.username ? capitalizeFirstLetter(postDoc.username) : null} {' '}</span>
                                    <Verification_Badge user={postDoc.user} /> <span>{/*TODO: GET RID OF THIS SPAN for some reason it makes the username bold*/}</span> 
                                </span>

                                {/* Post Timestamp */}
                                <span className="post__headerSpecial">
                                    <a href={
                                        redirect_on_click ? `/review/${postDoc._id}` : 
                                        !post ? `/review/${postDoc.post_commented_on}` :  // if this is a review comment only redirect to post_commented_on if not already on review page
                                        null
                                    }>
                                        {formatDistanceToNow(postDoc.date) === 'less than a minute' ? 'now' : `${formatDistanceToNow(postDoc.date)} ago`}
                                    </a>
                                </span>
                            </h3>

                            {(!replyToPost && postDoc && postDoc.to_user?.username) && (
                                <div className='replying_to_title'>
                                    Review for {' '}
        
                                    <a href={`/user/${postDoc.to_user._id}`}>
                                        {postDoc.to_user.first_name} {postDoc.to_user.last_name && ' ' + postDoc.to_user.last_name.charAt(0).toUpperCase()}.
                                    </a>
                                </div>
                            )}

                            {(replyToPost && replyToPost.user?.username)&& (
                                <div className='replying_to_title'>
                                    Replying to review
                                </div>
                            )}
                        </div>

                        {/* Post Text */}
                        {textState !== null && (
                            <div className={`post__headerDescription ${hasImg ? 'withImgs' : ''}`}>
                                {/* <p>{postDoc.text ? postDoc.text : null}</p> */}

                                {!isExpanded && shouldTruncate 
                                    ? <div dangerouslySetInnerHTML={{__html: textState}} /> 
                                    : <div dangerouslySetInnerHTML={{__html: rawTextState}} />}

                                {shouldTruncate && (
                                    <span 
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className='post_see_more_btn'
                                    >
                                        {isExpanded ? 'See less' : 'See more'}
                                    </span>
                                )}
                            </div>
                        )}
                        
                    </div>

                    {/* Post Images */}
                    <ImageBlock 
                        detailPost={postDoc} 
                        setHasImg={setHasImg} 
                        setCheckHasImg={setCheckHasImg} 
                        checkHasImg={checkHasImg} 
                        executeScroll={executeScroll} 
                        redirect_on_click={redirect_on_click}
                        setImgHeight={setImgHeight}
                        isPreview={isPreview}
                    />
                    {/* {postDoc.img && <img src={postDoc.img} alt="" />} */}

                    {/* Spacer, b/w post images and footer, becuase imageBlock is position:absolute  */}
                    {hasImg && (
                        <div style={{height: `${imgHeight}px`}}/>
                    )}
                    
                    {/* Post Actions */}
                    {!isComment && (
                        <div className={`post__footer ${postDoc.isSponsored && (postDoc.cta.type === "website" || postDoc.cta.type === "message") ? 'cta_btn' : ''} ${hasImg ? 'hasImg' : ''}`}>

                            {/* Like Button */}
                            {auth.user && postDoc.user._id !== auth.user_id ? (
                                <div className={liked ? "active" : "" } onClick={() => handleLike(postDoc._id)}>
                                    {!isPreview ? (
                                        <button className={liked ? "secondaryBtn post active like" : "secondaryBtn post like" }>
                                            <div>
                                                {/* Icon */}
                                                {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}

                                                {/* Button Text & like count */}
                                                <span>{liked ? 'Liked!' : 'Like'} {postDoc.likes.length > 0 && (
                                                    <span style={liked ? {display:'none'}: {fontSize:'11px'}}>({postDoc.likes.length})</span>
                                                )}</span>
                                            </div>
                                        </button>
                                    ) : (
                                        <button className="secondaryBtn post like">
                                            <div>
                                                {/* Icon */}
                                                <FavoriteBorderIcon fontSize="small" />

                                                {/* Button Text & like count */}
                                                <span>Like</span>
                                            </div>
                                        </button>
                                    )}
                                </div>
                            ) : (
                                // Placeholder
                                <div></div>
                            )}

                            {/* Comment Button */}
                            
                            {auth.user && postDoc.to_user?._id === auth.user_id && (
                                <div>
                                    <button 
                                        disabled={isPreview}
                                        onClick={() => window.location.href = `/review/${postDoc._id}`}
                                        className="secondaryBtn post"
                                    >
                                        <div>
                                            {/* Icon */}
                                            <ChatBubbleOutlineIcon fontSize="small" />

                                            {/* Button Text */}
                                            <span>Comment</span>
                                        </div>
                                    </button>
                                </div>
                            )}

                            {/* Share Button */}
                            {auth.user && postDoc.user._id !== auth.user_id && (
                                <div style={{position: 'relative'}}>
                                    <button disabled={isPreview} onClick={() => setShareDropdown(!shareDropdown)} className="secondaryBtn post share">
                                        <div>
                                            {/* Icon */}
                                            <ShareIcon fontSize="small" />

                                            {/* Button Text */}
                                            <span>Share</span>
                                        </div>
                                    </button>

                                    {/* Share Dropdown */}
                                    <div className={shareDropdown ? "edit-dropdown active" : "edit-dropdown"} style={{height: menuHeight}}>
                                        <div className="menu">

                                            {/* Copy Link Menu Item */}
                                            <CopyToClipboard 
                                                text={shareLink}
                                                onCopy={handleShareCopy}
                                            >
                                                <div className="menu-item">
                                                    {!copied ? (
                                                        <Fragment>
                                                            <LinkIcon />
                                                            Copy link
                                                        </Fragment>
                                                    ) : (
                                                        <Fragment>
                                                            <CheckIcon />
                                                            Copied!
                                                        </Fragment>
                                                    )}
                                                </div>
                                            </CopyToClipboard>
                                        </div>
                                    </div>

                                    {/* Hidden Overlay for share dropdown */}
                                    <div
                                        className={`hidden-overlay ${shareDropdown ? "show" : ""}`}
                                        onClick={() => setShareDropdown(!shareDropdown)}
                                    />
                                </div>
                            )}
                        
                        </div>
                    )}
                </div>

                {/* Post Overlay to detect when clicked and redirect to postPage */}
                <div 
                    className="post__overlay" 
                    onClick={() => 
                        redirect_on_click ? (window.location.href = `/review/${postDoc._id}`) : 
                        !post ? (window.location.href = `/review/${postDoc.post_commented_on}`) : // if this is a review comment only redirect to post_commented_on if not already on review page
                        {}}
                ></div>
            </div>

            {/* Delete Post Modal */}
            <ModalContainer show={deleteModal} onClose={toggleDeleteModal}>
                <Modal>

                    {/* Modal Title */}
                    <div>
                        <h2>Delete {!isComment ? 'Review' : 'Comment'}?</h2>
                    </div>

                    {/* Modal Description */}
                    <p>This can’t be undone and it will be removed from the {!isComment ? "user's profile" : 'review'}.</p>
                    
                    {/* Modal Actions */}
                    <Button 
                        onClick={handleDelete}
                        variant="outlined" 
                        className="sidebar__tweet danger"
                        fullWidth
                    >
                        Delete
                    </Button>
                    <Button 
                        onClick={toggleDeleteModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost blackText"
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Modal>
            </ModalContainer>

            {/* Copied Link Modal */}
            <ModalContainer show={copiedModal} onClose={toggleCopiedModal} white>
                <Modal border>

                    {/* Modal Title */}
                    <div>
                        <h2>Link Copied!</h2>
                    </div>

                    {/* Modal Description */}
                    <p>You can share this link with anyone.</p>

                    {/* Modal Action */}
                    <Button 
                        onClick={toggleCopiedModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost"
                        fullWidth
                    >
                        Close
                    </Button>
                </Modal>
            </ModalContainer>

            {/* Create Message confirmation Modal */}  
            <ModalContainer show={messageModal} onClose={toggleMessageModal}>
                <Modal>

                    {/* Modal Title */}
                    <div>
                        <h2>Start Conversation?</h2>
                    </div>

                    {/* Modal Description */}
                    <p>Start a private conversation with <b style={{color: '#333'}}>{postDoc.username ? postDoc.username : 'this person'}</b></p>
                    
                    {/* Modal Actions */}
                    <Button 
                        onClick={continueMessage}
                        variant="outlined" 
                        className="sidebar__tweet black"
                        fullWidth
                    >
                        Start Conversation
                    </Button>
                    <Button 
                        onClick={toggleMessageModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost blackText"
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Modal>
            </ModalContainer>
        </Fragment>
    )
}

Review.propTypes = {
    getUserById: PropTypes.func.isRequired, // Required prop: Function to get user by ID
    addLike_Review: PropTypes.func.isRequired, // Required prop: Function to add a like
    deleteReview: PropTypes.func.isRequired, // Required prop: Function to delete a post
    auth: PropTypes.object.isRequired, // Required prop: Object representing the authenticated user
    post: PropTypes.object.isRequired,
    nav: PropTypes.object.isRequired,
    setAlert: PropTypes.func.isRequired, // Required prop: Function to set an alert
    createChat: PropTypes.func.isRequired,
    toggleAuthModal: PropTypes.func.isRequired // Required prop: Function to toggle the authentication modal
}

const mapStateToProps = state => ({
    // Mapping the auth state from the Redux store to the 'auth' prop
    auth: state.auth,
    post: state.post,
    nav: state.nav
});

export default connect(mapStateToProps, { 

    // Connecting actions from redux to the component
    getUserById, 
    addLike_Review, 
    deleteReview, 
    setAlert, 
    createChat,
    toggleAuthModal 
})(Review);
